import React, { Component } from 'react';
import Store from 'eas-react-store';
import i18n from './index';

/**
 * Renders the application root keyed by the active language.
 *
 * withTranslation() alone is not enough for the signed-in app: several components build their
 * dropdown labels in their constructor, and re-running render() does not rebuild those. Keying on
 * the language remounts the tree so every constructor re-runs. That is safe because eas-react-store
 * is a global singleton living outside the React tree, so a remount loses no fetched data and
 * refetches nothing.
 *
 * Pass remountOnLanguageChange={false} for a tree that re-renders correctly on its own. The login
 * flow does: it is function components using useTranslation throughout, and remounting it would
 * throw away the email the visitor has already typed and send them back to the first step — which
 * is exactly when the organisation lookup changes the language. If a class component with
 * constructor-built labels is ever added to that tree, this has to be revisited.
 */
class LocalisedRoot extends Component {
    constructor(props) {
        super(props);

        this.state = {
            language: i18n.language
        };

        this.handleLanguageChanged = this.handleLanguageChanged.bind(this);
    }

    componentDidMount() {
        i18n.on('languageChanged', this.handleLanguageChanged);
    }

    componentWillUnmount() {
        i18n.off('languageChanged', this.handleLanguageChanged);
    }

    componentDidUpdate(previousProps, previousState) {
        if (previousState.language === this.state.language || !this.shouldRemount()) {
            return;
        }

        // connectStore pushes every instance into Store.components in its constructor and never
        // removes it; componentWillUnmount only flips hasMounted. Remounting the tree on each
        // language switch would therefore grow that array without bound, and updateStore() walks it
        // on every state change. We prune here rather than patching the eas-react-store git
        // dependency. By the time componentDidUpdate runs, the old tree has unmounted
        // (hasMounted === false) and the new tree has mounted (hasMounted === true).
        if (Array.isArray(Store.components)) {
            Store.components = Store.components.filter(component => component.hasMounted);
        }
    }

    shouldRemount() {
        return this.props.remountOnLanguageChange !== false;
    }

    handleLanguageChanged(language) {
        this.setState({ language: language });
    }

    render() {
        const Root = this.props.component;

        if (!this.shouldRemount()) {
            return <Root />;
        }

        return <Root key={this.state.language} />;
    }
}

export default LocalisedRoot;
